import React from 'react';
import { ContractStage, ContractStageWrite } from '../services/api';
import { ClosingStatusIcon } from './ClosingStatusIcon';
import { formatDateShort } from '../utils/table';
import './ContractStagesTable.css';

export interface StageDraft {
  key: string;
  id: number | null;
  name: string;
  result: string;
  planned_date_start: string;
  planned_date_end: string;
  durationMonths: string;
  durationDays: string;
  contractId: number | null;
  extendsStageKey: string | null;
  locked?: boolean;
  accountingClosingDate?: string;
}

function parseIsoDate(value: string): Date | null {
  const trimmed = value.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return null;
  const date = new Date(`${trimmed}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatIsoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function parseDurationPart(value: string | undefined): number | null {
  const trimmed = (value ?? '').trim();
  if (!trimmed) return null;
  if (!/^\d+$/.test(trimmed)) return null;
  return Number(trimmed);
}

function hasDurationInput(row: Pick<StageDraft, 'durationMonths' | 'durationDays'>): boolean {
  return parseDurationPart(row.durationMonths) != null || parseDurationPart(row.durationDays) != null;
}

function addDurationToDate(startIso: string, months: number, days: number): string | null {
  const start = parseIsoDate(startIso);
  if (!start) return null;
  const result = new Date(start.getTime());
  result.setMonth(result.getMonth() + months);
  result.setDate(result.getDate() + days);
  if (Number.isNaN(result.getTime())) return null;
  return formatIsoDate(result);
}

function calendarDuration(startIso: string, endIso: string): { months: number; days: number } | null {
  const start = parseIsoDate(startIso);
  const end = parseIsoDate(endIso);
  if (!start || !end || end.getTime() < start.getTime()) return null;
  let months = 0;
  const cursor = new Date(start.getTime());
  while (months < 1200) {
    const next = new Date(cursor.getTime());
    next.setMonth(next.getMonth() + 1);
    if (next.getTime() > end.getTime()) break;
    cursor.setTime(next.getTime());
    months += 1;
  }
  const days = Math.round((end.getTime() - cursor.getTime()) / 86_400_000);
  return { months, days };
}

function durationFromDates(startIso: string, endIso: string): { months: string; days: string } | null {
  const duration = calendarDuration(startIso, endIso);
  if (!duration) return null;
  return { months: String(duration.months), days: String(duration.days) };
}

function formatStageDuration(months: number, days: number): string {
  const parts: string[] = [];
  if (months > 0) parts.push(`${months} мес.`);
  if (days > 0 || months === 0) parts.push(`${days} дн.`);
  return parts.join(' ');
}

function stageDurationLabel(row: StageDraft): string {
  const fromDates = calendarDuration(row.planned_date_start, row.planned_date_end);
  if (fromDates) return formatStageDuration(fromDates.months, fromDates.days);
  if (hasDurationInput(row)) {
    return formatStageDuration(parseDurationPart(row.durationMonths) ?? 0, parseDurationPart(row.durationDays) ?? 0);
  }
  return '—';
}

function resolveStageEndDate(row: StageDraft): string {
  const explicit = row.planned_date_end.trim();
  if (explicit) return explicit;
  if (!row.planned_date_start.trim() || !hasDurationInput(row)) return '';
  return addDurationToDate(
    row.planned_date_start,
    parseDurationPart(row.durationMonths) ?? 0,
    parseDurationPart(row.durationDays) ?? 0,
  ) ?? '';
}

function patchFromStartChange(row: StageDraft, start: string): Partial<StageDraft> {
  const patch: Partial<StageDraft> = { planned_date_start: start };
  if (start && row.planned_date_end) {
    const derived = durationFromDates(start, row.planned_date_end);
    if (derived) {
      patch.durationMonths = derived.months;
      patch.durationDays = derived.days;
    } else {
      patch.durationMonths = '';
      patch.durationDays = '';
    }
    return patch;
  }
  if (start && !row.planned_date_end && hasDurationInput(row)) {
    const end = addDurationToDate(
      start,
      parseDurationPart(row.durationMonths) ?? 0,
      parseDurationPart(row.durationDays) ?? 0,
    );
    if (end) patch.planned_date_end = end;
  }
  return patch;
}

function patchFromEndChange(row: StageDraft, end: string): Partial<StageDraft> {
  const patch: Partial<StageDraft> = { planned_date_end: end };
  if (row.planned_date_start && end) {
    const derived = durationFromDates(row.planned_date_start, end);
    if (derived) {
      patch.durationMonths = derived.months;
      patch.durationDays = derived.days;
    } else {
      patch.durationMonths = '';
      patch.durationDays = '';
    }
  }
  return patch;
}

function patchFromDurationChange(row: StageDraft, months: string, days: string): Partial<StageDraft> {
  const patch: Partial<StageDraft> = { durationMonths: months, durationDays: days };
  if (row.planned_date_start && (months !== '' || days !== '')) {
    const end = addDurationToDate(row.planned_date_start, months === '' ? 0 : Number(months), days === '' ? 0 : Number(days));
    if (end) patch.planned_date_end = end;
  }
  return patch;
}

export function newStageDraftKey(): string {
  return `st-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function emptyStageDraft(contractId: number | null = null): StageDraft {
  return {
    key: newStageDraftKey(),
    id: null,
    name: '',
    result: '',
    planned_date_start: '',
    planned_date_end: '',
    durationMonths: '',
    durationDays: '',
    contractId,
    extendsStageKey: null,
    locked: false,
    accountingClosingDate: '',
  };
}

export function stagesFromServer(rows: ContractStage[]): StageDraft[] {
  const drafts: StageDraft[] = rows.map((row) => {
    const start = row.planned_date_start ?? '';
    const end = row.planned_date_end ?? '';
    const derived = durationFromDates(start, end);
    return {
      key: `s-${row.id}`,
      id: row.id,
      name: row.name,
      result: row.result ?? '',
      planned_date_start: start,
      planned_date_end: end,
      durationMonths: derived?.months ?? '',
      durationDays: derived?.days ?? '',
      contractId: row.contract_id,
      extendsStageKey: null,
      locked: Boolean(row.accounting_closed),
      accountingClosingDate: row.accounting_closing_date ?? '',
    };
  });
  const keyById = new Map<number, string>();
  for (const draft of drafts) {
    if (draft.id != null) keyById.set(draft.id, draft.key);
  }
  return drafts.map((draft, index) => {
    const extendsId = rows[index].extends_stage_id;
    return {
      ...draft,
      extendsStageKey: extendsId != null ? keyById.get(extendsId) ?? null : null,
    };
  });
}

export function stagesToWrite(drafts: StageDraft[]): ContractStageWrite[] {
  const byKey = new Map(drafts.map((row) => [row.key, row]));
  return drafts.map((row) => {
    const parent = row.extendsStageKey ? byKey.get(row.extendsStageKey) : null;
    return {
      id: row.id,
      client_key: row.key,
      contract_id: row.contractId,
      name: row.name.trim(),
      result: row.result.trim() || null,
      planned_date_start: row.planned_date_start,
      planned_date_end: resolveStageEndDate(row),
      extends_stage_id: parent?.id ?? null,
      extends_client_key: row.extendsStageKey,
    };
  });
}

export interface StageContractOption {
  id: number;
  label: string;
  hierarchyLevel: number;
}

export interface StageDraftGroup {
  parent: StageDraft;
  extensions: StageDraft[];
}

export function groupStageDrafts(drafts: StageDraft[]): StageDraftGroup[] {
  const byKey = new Map(drafts.map((row) => [row.key, row]));
  const extensionsByParent = new Map<string, StageDraft[]>();
  const nestedKeys = new Set<string>();
  for (const row of drafts) {
    const parentKey = row.extendsStageKey;
    if (!parentKey || parentKey === row.key || !byKey.has(parentKey)) continue;
    const list = extensionsByParent.get(parentKey) ?? [];
    list.push(row);
    extensionsByParent.set(parentKey, list);
    nestedKeys.add(row.key);
  }
  const groups: StageDraftGroup[] = [];
  for (const row of drafts) {
    if (nestedKeys.has(row.key)) continue;
    groups.push({ parent: row, extensions: extensionsByParent.get(row.key) ?? [] });
  }
  return groups;
}

export function validateStageDrafts(
  drafts: StageDraft[],
  options?: { contracts?: StageContractOption[] },
): string | null {
  if (drafts.length === 0) {
    return 'Укажите хотя бы один этап работ';
  }
  const contracts = options?.contracts ?? [];
  const byKey = new Map(drafts.map((row) => [row.key, row]));
  const contractById = new Map(contracts.map((row) => [row.id, row]));
  const topLevelIndex = new Map<string, number>();
  groupStageDrafts(drafts).forEach((group, index) => {
    topLevelIndex.set(group.parent.key, index + 1);
  });
  for (let index = 0; index < drafts.length; index += 1) {
    const row = drafts[index];
    const n = topLevelIndex.get(row.key) ?? index + 1;
    if (!row.name.trim()) return `Этап ${n}: укажите название`;
    if (contracts.length > 0 && row.contractId == null) return `Этап ${n}: выберите договор`;
    if (!row.planned_date_start) return `Этап ${n}: укажите дату начала`;
    const end = resolveStageEndDate(row);
    if (!end) return `Этап ${n}: укажите крайний срок или продолжительность`;
    if (end < row.planned_date_start) {
      return `Этап ${n}: крайний срок раньше даты начала`;
    }
    if (!row.extendsStageKey) continue;
    const parent = byKey.get(row.extendsStageKey);
    if (!parent) return `Этап ${n}: этап для продления не найден`;
    if (parent.key === row.key) return `Этап ${n}: нельзя продлевать сам себя`;
    const childContract = row.contractId != null ? contractById.get(row.contractId) : null;
    const parentContract = parent.contractId != null ? contractById.get(parent.contractId) : null;
    if (childContract && childContract.hierarchyLevel !== 2) {
      return `Этап ${n}: продление может быть только у ДС`;
    }
    if (parentContract && parentContract.hierarchyLevel !== 1) {
      return `Этап ${n}: можно продлевать только этап основного договора`;
    }
  }
  return null;
}

export function contractDatesFromStages(drafts: StageDraft[]): { start_date: string; end_date: string } {
  const first = drafts[0];
  const last = drafts[drafts.length - 1];
  return {
    start_date: first?.planned_date_start ?? '',
    end_date: last ? resolveStageEndDate(last) : '',
  };
}

export function datesFromBoundStages(
  rows: Array<{ planned_date_start?: string | null; planned_date_end?: string | null }>,
): { start_date: string; end_date: string } {
  const starts = rows
    .map((row) => row.planned_date_start?.trim() ?? '')
    .filter(Boolean)
    .sort();
  const ends = rows
    .map((row) => row.planned_date_end?.trim() ?? '')
    .filter(Boolean)
    .sort();
  return {
    start_date: starts[0] ?? '',
    end_date: ends[ends.length - 1] ?? '',
  };
}

export interface StageBindOption {
  id: string;
  label: string;
  memberIds?: string[];
}

function optionMemberIds(option: StageBindOption): string[] {
  return option.memberIds && option.memberIds.length > 0 ? option.memberIds : [option.id];
}

const StageBindItem: React.FC<{
  option: StageBindOption;
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  canEdit: boolean;
  disabled: boolean;
}> = ({ option, selectedIds, onChange, canEdit, disabled }) => {
  const selected = new Set(selectedIds);
  const ids = optionMemberIds(option);
  const selectedCount = ids.filter((id) => selected.has(id)).length;
  const checked = ids.length > 0 && selectedCount === ids.length;
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = selectedCount > 0 && !checked;
    }
  }, [checked, selectedCount]);

  return (
    <label className="contract-stages__bind-item">
      <input
        ref={inputRef}
        type="checkbox"
        checked={checked}
        disabled={!canEdit || disabled}
        onChange={() => {
          if (checked) {
            onChange(selectedIds.filter((id) => !ids.includes(id)));
            return;
          }
          const next = new Set(selectedIds);
          for (const id of ids) next.add(id);
          onChange([...next]);
        }}
      />
      <span>{option.label}</span>
    </label>
  );
};

export const StageBindPicker: React.FC<{
  options: StageBindOption[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  canEdit: boolean;
  disabled?: boolean;
  emptyHint?: string;
}> = ({
  options,
  selectedIds,
  onChange,
  canEdit,
  disabled = false,
  emptyHint = 'Этапы не заданы',
}) => {
  if (options.length === 0) {
    return <p className="contractor-detail__muted">{emptyHint}</p>;
  }
  return (
    <div className="contract-stages__bind">
      {options.map((option) => (
        <StageBindItem
          key={option.id}
          option={option}
          selectedIds={selectedIds}
          onChange={onChange}
          canEdit={canEdit}
          disabled={disabled}
        />
      ))}
    </div>
  );
};

export interface StageTaskOption {
  id: string;
  label: string;
  memberIds?: string[];
}

interface ContractStagesEditorProps {
  drafts: StageDraft[];
  onChange: (next: StageDraft[]) => void;
  canEdit: boolean;
  disabled?: boolean;
  taskOptions?: StageTaskOption[];
  boundTaskIdsByStageId?: Record<number, string[]>;
  onBoundTasksChange?: (stageId: number, taskIds: string[]) => void;
  canEditTasks?: boolean;
  compactMetaColumns?: boolean;
  contractOptions?: StageContractOption[];
  defaultContractId?: number | null;
}

export const ContractStagesEditor: React.FC<ContractStagesEditorProps> = ({
  drafts,
  onChange,
  canEdit,
  disabled = false,
  taskOptions,
  boundTaskIdsByStageId = {},
  onBoundTasksChange,
  canEditTasks = false,
  compactMetaColumns = false,
  contractOptions = [],
  defaultContractId = null,
}) => {
  const updateRow = (
    key: string,
    patch: Partial<StageDraft> | ((row: StageDraft) => Partial<StageDraft>),
  ): void => {
    onChange(
      drafts.map((row) => {
        if (row.key !== key) return row;
        const next = typeof patch === 'function' ? patch(row) : patch;
        return { ...row, ...next };
      }),
    );
  };
  const showTasksColumn = taskOptions != null;
  const showContractColumn = contractOptions.length > 0;
  const hasLevel2 = contractOptions.some((option) => option.hierarchyLevel === 2);
  const firstDsId = contractOptions.find((option) => option.hierarchyLevel === 2)?.id ?? null;
  const contractById = new Map(contractOptions.map((option) => [option.id, option]));
  const groups = groupStageDrafts(drafts);
  const colCount =
    (canEdit ? 8 : 7) + (showTasksColumn ? 1 : 0) + (showContractColumn ? 1 : 0);

  const contractLabel = (contractId: number | null): string => {
    if (contractId == null) return '—';
    return contractById.get(contractId)?.label ?? '—';
  };

  const isLevel2 = (contractId: number | null): boolean =>
    contractId != null && contractById.get(contractId)?.hierarchyLevel === 2;

  const mainStageOptions = (excludeKey: string): StageDraft[] =>
    drafts.filter((row) => {
      if (row.key === excludeKey || row.extendsStageKey) return false;
      const level = row.contractId != null ? contractById.get(row.contractId)?.hierarchyLevel : 1;
      return level === 1;
    });

  const renderTasksCell = (row: StageDraft): React.ReactNode => {
    if (row.id == null) {
      return (
        <span className="contractor-detail__muted" title="Сначала сохраните этап">
          —
        </span>
      );
    }
    const selectedIds = boundTaskIdsByStageId[row.id] ?? [];
    if (!taskOptions || taskOptions.length === 0) {
      return <span className="contractor-detail__muted">—</span>;
    }
    const canBind = canEdit && canEditTasks && Boolean(onBoundTasksChange);
    if (!canBind) {
      const labels = taskOptions
        .filter((option) => optionMemberIds(option).some((id) => selectedIds.includes(id)))
        .map((option) => option.label);
      return labels.length > 0 ? labels.join(', ') : '—';
    }
    return (
      <StageBindPicker
        options={taskOptions}
        selectedIds={selectedIds}
        onChange={(ids) => onBoundTasksChange?.(row.id as number, ids)}
        canEdit
        disabled={disabled}
        emptyHint="Нет задач по договору"
      />
    );
  };

  const renderRow = (
    row: StageDraft,
    numberLabel: string,
    isExtension: boolean,
    extensionCount: number,
  ): React.ReactNode => {
    const boundCount = row.id != null ? (boundTaskIdsByStageId[row.id] ?? []).length : 0;
    const deleteBlockedByTasks = boundCount > 0 && !canEditTasks;
    const deleteBlockedByExtensions = !isExtension && extensionCount > 0;
    const rowIsLevel2 = isLevel2(row.contractId);
    const parentOptions = mainStageOptions(row.key);
    return (
      <tr
        key={row.key}
        className={isExtension ? 'contract-stages__row--extension' : undefined}
      >
        <td className="contract-stages__col-num">
          {isExtension ? <span className="contract-stages__ext-mark">└ {numberLabel}</span> : numberLabel}
        </td>
        {showContractColumn ? (
          <td className="contract-stages__col-contract">
            {canEdit ? (
              <div className="contract-stages__contract-cell">
                <select
                  className="contractor-detail__field task-detail__cell-input contract-stages__contract-select"
                  value={row.contractId != null ? String(row.contractId) : ''}
                  disabled={disabled}
                  onChange={(e) => {
                    const nextId = e.target.value ? Number(e.target.value) : null;
                    const nextIsMain = nextId != null && contractById.get(nextId)?.hierarchyLevel === 1;
                    onChange(
                      drafts.map((item) => {
                        if (item.key === row.key) {
                          return {
                            ...item,
                            contractId: nextId,
                            extendsStageKey: nextId != null && isLevel2(nextId) ? item.extendsStageKey : null,
                          };
                        }
                        if (!nextIsMain && item.extendsStageKey === row.key) {
                          return { ...item, extendsStageKey: null };
                        }
                        return item;
                      }),
                    );
                  }}
                  aria-label={`Договор этапа ${numberLabel}`}
                >
                  <option value="">Договор</option>
                  {contractOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {hasLevel2 && rowIsLevel2 ? (
                  <select
                    className="contractor-detail__field task-detail__cell-input contract-stages__contract-select"
                    value={row.extendsStageKey ?? ''}
                    disabled={disabled}
                    onChange={(e) => updateRow(row.key, { extendsStageKey: e.target.value || null })}
                    aria-label={`Продление этапа ${numberLabel}`}
                  >
                    <option value="">Не продление</option>
                    {parentOptions.map((parent) => (
                      <option key={parent.key} value={parent.key}>
                        {groups.findIndex((group) => group.parent.key === parent.key) + 1}.{' '}
                        {parent.name.trim() || 'Без названия'}
                      </option>
                    ))}
                  </select>
                ) : null}
              </div>
            ) : (
              contractLabel(row.contractId)
            )}
          </td>
        ) : null}
        <td className="contract-stages__col-name task-detail__td-text">
          {canEdit ? (
            <input
              type="text"
              className="contractor-detail__field"
              value={row.name}
              disabled={disabled}
              onChange={(e) => updateRow(row.key, { name: e.target.value })}
              aria-label={`Название этапа ${numberLabel}`}
            />
          ) : (
            row.name || '—'
          )}
        </td>
        <td className="contract-stages__col-result task-detail__td-text">
          {canEdit ? (
            <input
              type="text"
              className="contractor-detail__field"
              value={row.result}
              disabled={disabled}
              onChange={(e) => updateRow(row.key, { result: e.target.value })}
              aria-label={`Результат этапа ${numberLabel}`}
            />
          ) : (
            row.result.trim() || '—'
          )}
        </td>
        {showTasksColumn ? (
          <td className="contract-stages__col-tasks task-detail__td-text">{renderTasksCell(row)}</td>
        ) : null}
        <td className="contract-stages__col-date">
          {canEdit ? (
            <input
              type="date"
              className="contractor-detail__field task-detail__cell-input"
              value={row.planned_date_start}
              disabled={disabled}
              onChange={(e) => updateRow(row.key, (item) => patchFromStartChange(item, e.target.value))}
              aria-label={`Начало этапа ${numberLabel}`}
            />
          ) : (
            formatDateShort(row.planned_date_start)
          )}
        </td>
        <td className="contract-stages__col-duration">
          {canEdit ? (
            <div className="contract-stages__duration">
              <input
                type="text"
                inputMode="numeric"
                className="contractor-detail__field task-detail__cell-input contract-stages__duration-input"
                value={row.durationMonths ?? ''}
                disabled={disabled}
                maxLength={3}
                onChange={(e) => {
                  const months = e.target.value.replace(/\D/g, '');
                  updateRow(row.key, (item) => patchFromDurationChange(item, months, item.durationDays ?? ''));
                }}
                aria-label={`Продолжительность этапа ${numberLabel}, месяцы`}
              />
              <span className="contract-stages__duration-unit">мес.</span>
              <input
                type="text"
                inputMode="numeric"
                className="contractor-detail__field task-detail__cell-input contract-stages__duration-input"
                value={row.durationDays ?? ''}
                disabled={disabled}
                maxLength={4}
                onChange={(e) => {
                  const days = e.target.value.replace(/\D/g, '');
                  updateRow(row.key, (item) => patchFromDurationChange(item, item.durationMonths ?? '', days));
                }}
                aria-label={`Продолжительность этапа ${numberLabel}, дни`}
              />
              <span className="contract-stages__duration-unit">дн.</span>
            </div>
          ) : (
            stageDurationLabel(row)
          )}
        </td>
        <td className="contract-stages__col-date">
          {canEdit ? (
            <input
              type="date"
              className="contractor-detail__field task-detail__cell-input"
              value={row.planned_date_end}
              disabled={disabled}
              onChange={(e) => updateRow(row.key, (item) => patchFromEndChange(item, e.target.value))}
              aria-label={`Крайний срок этапа ${numberLabel}`}
            />
          ) : (
            formatDateShort(row.planned_date_end)
          )}
        </td>
        <td className="contract-stages__col-closed">
          <ClosingStatusIcon
            status={row.locked ? 'closed' : 'open'}
            closingDate={row.accountingClosingDate}
          />
        </td>
        {canEdit ? (
          <td className="contract-stages__col-actions">
            {!isExtension && hasLevel2 && firstDsId != null && !isLevel2(row.contractId) ? (
              <button
                type="button"
                className="contract-stages__extend-btn"
                onClick={() =>
                  onChange([
                    ...drafts,
                    {
                      ...emptyStageDraft(firstDsId),
                      extendsStageKey: row.key,
                      name: row.name,
                    },
                  ])
                }
                disabled={disabled}
                title="Добавить продление ДС"
                aria-label={`Добавить продление этапа ${numberLabel}`}
              >
                +ДС
              </button>
            ) : null}
            <button
              type="button"
              className="contract-detail__calendar-delete"
              onClick={() => onChange(drafts.filter((item) => item.key !== row.key))}
              disabled={disabled || Boolean(row.locked) || deleteBlockedByTasks || deleteBlockedByExtensions}
              title={
                row.locked
                  ? 'Нельзя удалить закрытый этап'
                  : deleteBlockedByTasks
                    ? 'Сначала отвяжите задачи'
                    : deleteBlockedByExtensions
                      ? 'Сначала отвяжите продления'
                      : undefined
              }
              aria-label={`Удалить этап ${numberLabel}`}
            >
              ×
            </button>
          </td>
        ) : null}
      </tr>
    );
  };

  return (
    <div className={`contract-stages${compactMetaColumns ? ' contract-stages--compact' : ''}`}>
      <table className="task-detail__mini-table task-detail__mini-table--compact contract-stages__table">
        <thead>
          <tr>
            <th className="contract-stages__col-num">№</th>
            {showContractColumn ? (
              <th className="contract-stages__col-contract">
                {compactMetaColumns ? (
                  <>
                    Договор
                    <br />
                    / ДС
                  </>
                ) : (
                  'Договор / ДС'
                )}
              </th>
            ) : null}
            <th className="contract-stages__col-name">Название</th>
            <th className="contract-stages__col-result">Результат</th>
            {showTasksColumn ? <th className="contract-stages__col-tasks">Задачи</th> : null}
            <th className="contract-stages__col-date">Начало</th>
            <th className="contract-stages__col-duration">
              {compactMetaColumns ? (
                <>
                  Продолжи-
                  <br />
                  тельность
                </>
              ) : (
                'Продолжительность'
              )}
            </th>
            <th className="contract-stages__col-date">
              {compactMetaColumns ? (
                <>
                  Крайний
                  <br />
                  срок
                </>
              ) : (
                'Крайний срок'
              )}
            </th>
            <th className="contract-stages__col-closed">
              {compactMetaColumns ? (
                <>
                  Закрыто
                  <br />
                  по Бухгалтерии
                </>
              ) : (
                'Закрыто по Бухгалтерии'
              )}
            </th>
            {canEdit ? <th className="contract-stages__col-actions" /> : null}
          </tr>
        </thead>
        <tbody>
          {drafts.length === 0 ? (
            <tr>
              <td colSpan={colCount} className="data-table__empty">
                Этапы не заданы
              </td>
            </tr>
          ) : null}
          {groups.map((group, groupIndex) => (
            <React.Fragment key={group.parent.key}>
              {renderRow(group.parent, String(groupIndex + 1), false, group.extensions.length)}
              {group.extensions.map((extension, extensionIndex) =>
                renderRow(extension, `${groupIndex + 1}.${extensionIndex + 1}`, true, 0),
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
      {canEdit ? (
        <button
          type="button"
          className="contract-detail__calendar-add"
          onClick={() => onChange([...drafts, emptyStageDraft(defaultContractId)])}
          disabled={disabled}
        >
          + Этап
        </button>
      ) : null}
    </div>
  );
};

interface ContractStagesClosingProps {
  stages: ContractStage[];
  canClose: boolean;
  closingStageId: number | null;
  onClose: (stageId: number) => void;
}

export const ContractStagesClosingTable: React.FC<ContractStagesClosingProps> = ({
  stages,
  canClose,
  closingStageId,
  onClose,
}) => {
  const nextOpen = stages.find((row) => !row.accounting_closed) ?? null;

  return (
    <table className="task-detail__mini-table task-detail__mini-table--compact contract-stages__table">
      <thead>
        <tr>
          <th className="contract-stages__col-num">№</th>
          <th>Название</th>
          <th className="contract-stages__col-date">Начало</th>
          <th className="contract-stages__col-date">Завершение</th>
          <th>Бухгалтерия</th>
          {canClose ? <th /> : null}
        </tr>
      </thead>
      <tbody>
        {stages.length === 0 ? (
          <tr>
            <td colSpan={canClose ? 6 : 5} className="data-table__empty">
              Этапы не заданы
            </td>
          </tr>
        ) : null}
        {stages.map((row) => {
          const isNext = nextOpen != null && nextOpen.id === row.id;
          return (
            <tr key={row.id}>
              <td className="contract-stages__col-num">{row.stage_number}</td>
              <td>{row.name}</td>
              <td className="contract-stages__col-date">{formatDateShort(row.planned_date_start)}</td>
              <td className="contract-stages__col-date">{formatDateShort(row.planned_date_end)}</td>
              <td>
                {row.accounting_closed
                  ? formatDateShort(row.accounting_closing_date)
                  : 'открыт'}
              </td>
              {canClose ? (
                <td>
                  {row.accounting_closed ? null : (
                    <button
                      type="button"
                      className="submit-button"
                      disabled={!isNext || closingStageId != null}
                      onClick={() => onClose(row.id)}
                    >
                      {closingStageId === row.id ? 'Закрытие…' : 'Закрыть'}
                    </button>
                  )}
                </td>
              ) : null}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};
